import { endpoints } from '@api'
import { Box, Flex, useBreakpointValue, useToast } from '@chakra-ui/react'
import { Loading } from '@components'
import { datadogLogs } from '@datadog/browser-logs'
import { useComponentRenderTracker, useExperienceManager } from '@hooks'
import { trackEvent } from '@lib/clinician-tracking'
import { useStoreActions } from 'easy-peasy'
import qs from 'query-string'
import queryString from 'query-string'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { UAParser } from 'ua-parser-js'
import {
  getOrganizationControllerGetSessionCreditQueryKey,
  useSessionControllerGetSessionRecap,
  useSessionControllerPostDiscardSession,
  useSessionControllerPostMarkCompleteV2,
  useSessionControllerTrackEvent
} from '~/clinician-api'
import { BlueNavLogo } from '../../images/brand/blue-nav-logo'
import { InSessionAssistPanel } from './components'
import { EndSessionModalV2 } from './components/end-session-modal-v2'
import { MobileSessionDrawer } from './components/mobile-assist-drawer'

import { ClinicianWaitingRoom } from './components/telehealth/clinician-waiting-room'
import { ClinicianTelehealthFooter } from './components/telehealth/components'
import { AdmitWaitingParticipantDialog } from './components/telehealth/components/admit-particiapnt-dialog'
import { ParticipantList } from './components/telehealth/components/participant-list'
import { SidePanel } from './components/telehealth/components/side-panel'
import {
  ActiveTelehealthSessionProvider,
  useActiveTelehealthSession
} from './components/telehealth/context/active-telehealth-session.context'
import {
  ClinicianDailyProvider,
  useClinicianDailyManager
} from './components/telehealth/context/clinician-daily-manager.context'
import {
  ClinicianTelehealthSessionProvider,
  useClinicianTelehealthSession
} from './components/telehealth/context/clinician-telehealth-session.context'
import { LayoutManager } from './components/telehealth/layouts'
import { WaitroomPopoverManager } from './components/telehealth/waiting-room-popover-manager'

export const TelehealthSessionActivity = () => {
  useComponentRenderTracker('Viewed Telehealth Session Page')

  const {
    isLoading: isLoadingActiveTelehealthSession,
    patient: client,
    isEndSessionModalOpen,
    setIsEndSessionModalOpen,
    setRecordingStartedAt,
    admitParticipantDialogProps,
    activeSidebarTab,
    toggleSidebar,
    isSidebarVisible,
    meetingToken
  } = useClinicianTelehealthSession()

  const {
    isInCall,
    endCall,
    configureRoom,
    isCallEnded,
    hasTranscriptionError
  } = useClinicianDailyManager()

  const {
    setRecordingStartedAt: setRecordingStartedAtActiveTelehealthSession
  } = useActiveTelehealthSession()

  const toast = useToast()

  const { sessionId, id: clientId }: { sessionId: string; id: string } =
    useParams()

  const history = useHistory()

  const { data: recap, isLoading: isLoadingSessionRecap } =
    useSessionControllerGetSessionRecap(sessionId, {
      query: {
        enabled: !!sessionId
      }
    })

  useEffect(() => {
    if (recap && recap.completedAt) {
      history.push(`/patient/${clientId}/completed-session/${sessionId}`)
    }
  }, [recap])

  useEffect(() => {
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          // @ts-ignore
          await navigator.wakeLock.request('screen')
          setWakeLockEnabled(true)
        }
      } catch (err) {
        datadogLogs.logger.warn('[Session] Unable to request wake lock', {
          err,
          ...logDataRef.current
        })
        setWakeLockEnabled(false)
      }
    }

    requestWakeLock()
  }, [])

  const queryClient = useQueryClient()
  const { data: user }: { data: any } = useQuery(
    endpoints.getUserAccount.getCacheId(),
    () => endpoints.getUserAccount.request()
  )

  const location = useLocation()
  const { context } = qs.parse(location.search)

  const { mutate: trackSessionEvent } = useSessionControllerTrackEvent()

  const handleTrackEvent = useCallback(
    (event: string, payload: object) => {
      if (!sessionId) {
        console.warn('No session ID found, unable to track event', {
          event,
          payload
        })
        return
      }
      trackSessionEvent({
        id: sessionId,
        data: { eventType: event, payload: payload as any }
      })
    },
    [sessionId, trackSessionEvent]
  )
  useEffect(() => {
    if (hasTranscriptionError) {
      datadogLogs.logger.error('dailyco_transcription_error', {
        ...logDataRef.current
      })
      handleTrackEvent('dailyco_transcription_error', {})
    }
  }, [hasTranscriptionError, handleTrackEvent])

  const [isEndingSession, setIsEndingSession] = useState(false)
  const [noteType, setNoteType] = useState('soap')
  const [wakeLockEnabled, setWakeLockEnabled] = useState(false)

  const isSmallScreen = useBreakpointValue({ base: true, sm: true, md: false })
  const {
    documentationAutomationFreeTierSessionLimitReached,
    isEvidenceBasedCareEnabled,
    isAdmin,
    isExtension,
    isWidget,
    isPlusPlanEnabled,
    memoryEnabled,
    isTelehealthEnabled,
    usageBasedPricingEnabled
  } = useExperienceManager()

  useEffect(() => {
    if (!isTelehealthEnabled) {
      history.replace(`/patient/${clientId}/session/${sessionId}`)
    }
  }, [isTelehealthEnabled])

  useEffect(() => {
    if (
      documentationAutomationFreeTierSessionLimitReached &&
      !usageBasedPricingEnabled
    ) {
      handleSessionLimitReached()
    }
  }, [documentationAutomationFreeTierSessionLimitReached])

  const handleSessionLimitReached = async () => {
    if (isAdmin) {
      if (isEvidenceBasedCareEnabled) {
        openDocAutomationPlanModal({
          clinicId: user.clinic_id
        })
      } else {
        const queryParams = queryString.stringify({
          redirectPath: location.pathname,
          prepGenerationSessionId: sessionId
        })

        history.push(`/settings/plans?${queryParams}`)
      }
    } else if (!usageBasedPricingEnabled) {
      openDocAutomationSessionLimitRecachedModal()
    }
  }

  const openDocAutomationPlanModal = useStoreActions(
    (actions: any) => actions.modals.manageScribePlan.openModal
  )

  const openDocAutomationSessionLimitRecachedModal = useStoreActions(
    (actions: any) => actions.modals.docAutomationSessionLimitReached.openModal
  )

  const deviceInfo = useMemo(() => new UAParser().getResult(), [])

  const logDataRef = useRef({})

  logDataRef.current = useMemo(
    () => ({
      // ...baseLogData,
      isHidden: document.hidden,
      clientId: client?.id,
      wakeLockEnabled,
      deviceInfo
    }),
    [client?.id, wakeLockEnabled, deviceInfo]
  )

  const { mutateAsync: endSession } = useSessionControllerPostMarkCompleteV2()

  const { mutateAsync: discardSession } =
    useSessionControllerPostDiscardSession()

  const { data: lastSession }: any = useQuery(
    [endpoints.getClinicianClientLastSession.getCacheId(), clientId],
    () =>
      endpoints.getClinicianClientLastSession.request({
        id: clientId
      })
  )

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        datadogLogs.logger.warn('[Session] Page is hidden', {
          ...logDataRef.current
        })
      } else {
        datadogLogs.logger.info('[Session] Page is visible', {
          ...logDataRef.current
        })
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  useEffect(() => {
    const clientNoteType = lastSession?.noteType
    const defaultNoteType =
      user?.clinic?.organization?.default_note_type || 'soap'

    setNoteType(clientNoteType || defaultNoteType)
  }, [user, lastSession])

  useEffect(() => {
    if (isInCall && isSmallScreen) {
      toggleSidebar('none')
    }
  }, [isSmallScreen, isInCall])

  useEffect(() => {
    if (isInCall) {
      ;(window as any).Intercom?.('update', {
        hide_default_launcher: true
      })
    }

    return () => {
      ;(window as any).Intercom?.('update', {
        hide_default_launcher: false
      })
    }
  }, [isInCall])

  const handleBeforeUnload = useMemo(() => {
    return () => {
      datadogLogs.logger.info(
        '[Session] Page refresh attempt',
        logDataRef.current
      )
      return 'Did you finish the session?'
    }
  }, [])

  useEffect(() => {
    window.onbeforeunload = handleBeforeUnload
    return () => {
      window.onbeforeunload = () => {}
    }
  }, [handleBeforeUnload])

  const handleEndSession = useCallback(
    async ({ noteType, useDemoNotesWithEBC, treatmentApproaches }: any) => {
      setIsEndingSession(true)
      await endCall()
      handleTrackEvent('generate_note_clicked', {})
      try {
        await endSession(
          {
            id: sessionId,
            data: {
              noteType,
              useDemoNotesWithEBC,
              shouldGenerateTxPlan: true,
              assistEnabled: isPlusPlanEnabled,
              memoryEnabled: memoryEnabled,
              interventionSuggestionsEnabled: true,
              treatmentApproaches
            }
          },
          {
            onSuccess() {
              trackEvent('Completed Session', { sessionId })
              queryClient.invalidateQueries(
                endpoints.getPaywallValidation.getCacheId()
              )
              const sessionCreditsQueryKey =
                getOrganizationControllerGetSessionCreditQueryKey(
                  user?.clinic?.organization_id || ''
                )

              queryClient.invalidateQueries(sessionCreditsQueryKey)
            }
          }
        )
      } catch (err) {
        datadogLogs.logger.info(
          '[Session] Error ending session',
          logDataRef.current
        )
      }

      setIsEndingSession(false)
      if (isExtension) {
        history.push(
          `/extension/patient/${client?.id}/completed-session/${sessionId}`
        )
      } else if (isWidget)
        history.push(
          `/widget/patient/${client?.id}/completed-session/${sessionId}`
        )
      else {
        const baseSessionUrl = `/patient/${client?.id}/completed-session/${sessionId}`
        const queryParams: Record<string, any> = {}
        if (client?.is_demo) {
          queryParams['isDemo'] = 'true'
        }

        if (context) {
          queryParams['context'] = context
        }

        const queryString =
          Object.keys(queryParams).length > 0
            ? `?${new URLSearchParams(queryParams).toString()}`
            : ''

        history.push(`${baseSessionUrl}${queryString}`)
      }
    },
    [
      client?.id,
      client?.is_demo,
      endSession,
      history,
      isExtension,
      isPlusPlanEnabled,
      isWidget,
      queryClient,
      sessionId
    ]
  )

  const handleDiscardSession = async () => {
    await endCall()
    setIsEndingSession(true)
    try {
      await discardSession({ id: sessionId })
    } catch (err) {
      datadogLogs.logger.info(
        '[Session] Error resetting meeting',
        logDataRef.current
      )
    }
    setIsEndingSession(false)

    history.replace('/sessions')

    toast({
      title: 'Session discarded',
      status: 'success',
      isClosable: true,
      duration: 2000
    })
  }

  const onOpenEndModal = () => setIsEndSessionModalOpen(true)
  const onCloseEndModal = () => setIsEndSessionModalOpen(false)

  useEffect(() => {
    if (recap?.recordingStartedAt) {
      setRecordingStartedAt(recap?.recordingStartedAt)
      setRecordingStartedAtActiveTelehealthSession(recap?.recordingStartedAt)
    }
  }, [recap?.recordingStartedAt, setRecordingStartedAt])

  useEffect(() => {
    if (
      meetingToken?.token &&
      recap?.dailyRoomUrl &&
      !isInCall &&
      !isCallEnded
    ) {
      if (recap?.recordingStartedAt) {
        toggleSidebar('none')
        configureRoom({
          url: recap.dailyRoomUrl,
          token: meetingToken.token,
          isResuming: true,
          isOwner: true
        })
      } else {
        configureRoom({
          url: recap.dailyRoomUrl,
          token: meetingToken.token,
          isOwner: true
        })
      }
    }
  }, [
    meetingToken?.token,
    recap?.dailyRoomUrl,
    recap?.recordingStartedAt,
    isInCall,
    isCallEnded
  ])

  if (
    (clientId && isLoadingActiveTelehealthSession) ||
    (sessionId && isLoadingSessionRecap)
  ) {
    return (
      <Flex
        bg="#F6F7FF"
        h="100%"
        w="100%"
        overflow="hidden"
        justify="center"
        align="center"
        position="relative"
      >
        <BlueNavLogo
          position="absolute"
          top="25px"
          left="32px"
          display={{ base: 'none', md: 'block' }}
        />
        <Loading />
      </Flex>
    )
  }

  return (
    <>
      <WaitroomPopoverManager />
      <Box
        bg={isInCall ? 'black' : '#F6F7FF'}
        h="100%"
        overflow="hidden"
        display="flex"
        flexDirection="column"
        data-testid="session-layout-parent"
        transition="background-color 0.5s"
        position="relative"
      >
        {!isInCall && (
          <BlueNavLogo
            position="absolute"
            top="25px"
            left="32px"
            display={{ base: 'none', md: 'block' }}
          />
        )}
        <Flex
          h="100%"
          flexDirection={{
            base: 'column',
            md: 'row'
          }}
          data-testid="session-layout"
          overflow="hidden"
        >
          <Flex
            justifyContent="center"
            flex="1"
            h="100%"
            w="100%"
            data-testid="ai-notetaker-panel"
            overflow="auto"
            transition="all 0.3s ease-in-out"
          >
            {isInCall && <LayoutManager />}
            {!isInCall && !isCallEnded && !recap?.recordingStartedAt && (
              <ClinicianWaitingRoom />
            )}
          </Flex>

          {isInCall && !isSmallScreen && (
            <SidePanel isVisible={isSidebarVisible}>
              {activeSidebarTab === 'assist' && (
                <InSessionAssistPanel
                  isTelehealthSession={true}
                  client={client}
                  toggleAssist={() => toggleSidebar(activeSidebarTab)}
                  lastSession={lastSession}
                />
              )}
              {activeSidebarTab === 'participants' && (
                <ParticipantList
                  user={user}
                  onClose={() => toggleSidebar(activeSidebarTab)}
                />
              )}
            </SidePanel>
          )}
        </Flex>
        {isInCall && isSmallScreen && (
          <MobileSessionDrawer isOpen={isSidebarVisible}>
            <>
              {activeSidebarTab === 'assist' && (
                <InSessionAssistPanel
                  isTelehealthSession={true}
                  client={client}
                  toggleAssist={() => toggleSidebar(activeSidebarTab)}
                  lastSession={lastSession}
                />
              )}
              {activeSidebarTab === 'participants' && (
                <ParticipantList
                  user={user}
                  onClose={() => toggleSidebar(activeSidebarTab)}
                />
              )}
            </>
          </MobileSessionDrawer>
        )}
        {isInCall && (
          <>
            <Box minH="60px" style={{ marginTop: 0 }} />
            <ClinicianTelehealthFooter
              onSessionEnd={onOpenEndModal}
              client={client}
              activeSidebarTab={activeSidebarTab}
              toggleSidebar={toggleSidebar}
            />
          </>
        )}
        <EndSessionModalV2
          isOpen={isEndSessionModalOpen}
          onClose={onCloseEndModal}
          noteType={noteType}
          treatmentApproaches={lastSession?.treatmentApproaches}
          onEnd={handleEndSession}
          onDiscard={handleDiscardSession}
          isEnding={isEndingSession}
          isUploadingAudio={false}
          showRecordingLengthWarning={!recap?.recordingStartedAt}
          isDailyTelehealthSession={true}
        />
        <AdmitWaitingParticipantDialog {...admitParticipantDialogProps} />
      </Box>
    </>
  )
}

const TelehealthSessionWithContext = ({ sessionId }: { sessionId: string }) => {
  const { isBackgroundBlurred, toggleBackgroundBlur } =
    useClinicianDailyManager()

  return (
    <ActiveTelehealthSessionProvider
      defaultSidebarTab="assist"
      isBackgroundBlurred={isBackgroundBlurred}
      toggleBackgroundBlur={toggleBackgroundBlur}
      isCallOwner={true}
      sessionId={sessionId}
    >
      <TelehealthSessionActivity />
    </ActiveTelehealthSessionProvider>
  )
}

export const TelehealthSessionActivityWrapper = () => {
  const { sessionId, id: patientId }: { sessionId: string; id: string } =
    useParams()

  return (
    <ClinicianTelehealthSessionProvider
      sessionId={sessionId}
      patientId={patientId}
    >
      <ClinicianDailyProvider>
        <TelehealthSessionWithContext sessionId={sessionId} />
      </ClinicianDailyProvider>
    </ClinicianTelehealthSessionProvider>
  )
}
